import type { Branch } from '@/api/types'
import { addDays, wallInstant, wallOf } from './calendar'
import { canonicalZone, DEFAULT_ZONE } from './timezones'

/// A report's period on the club's wall calendar — one cut for every report
/// («Товари → Продажі», «Огляд»), so the same «Цей місяць» is the same two
/// instants whichever service is asked. SPEC: specs/shop.md §2.6,
/// specs/reports.md §9.

/// - `today`, `week` (today and the six days before it), `month` (today and
///   the 29 before it) — they end today and include it;
/// - `thisMonth` — the 1st to today; `lastMonth` — the whole of the last one;
/// - `year` — 1 January to today.
export type PeriodKind = 'today' | 'week' | 'month' | 'thisMonth' | 'lastMonth' | 'year'

/// A period on the club's wall calendar: its first and last day, both
/// included — what the page shows — and `[from, to)` as instants, what the
/// server is asked for (half-open, specs/shop.md §2.6).
export type PeriodRange = Readonly<{ firstDay: string; lastDay: string; from: string; to: string }>

/// The first and last day of a period that is about `today`.
const periodDays = (period: PeriodKind, today: string): readonly [string, string] => {
  const firstOfMonth = `${today.slice(0, 7)}-01`
  switch (period) {
    case 'today':
      return [today, today]
    case 'week':
      return [addDays(today, -6), today]
    case 'month':
      return [addDays(today, -29), today]
    case 'thisMonth':
      return [firstOfMonth, today]
    case 'lastMonth': {
      const lastOfPrevious = addDays(firstOfMonth, -1)
      return [`${lastOfPrevious.slice(0, 7)}-01`, lastOfPrevious]
    }
    case 'year':
      return [`${today.slice(0, 4)}-01-01`, today]
  }
}

/// A period by the CLUB's calendar — its IANA zone, never the browser's — so
/// a day starts at the club's midnight and is 23 or 25 hours long on the
/// nights the clocks change (domain/calendar.ts `wallInstant`). «7 днів» and
/// «30 днів» end today and include it.
export const periodRange = (period: PeriodKind, zone: string, now: Date = new Date()): PeriodRange => {
  const [firstDay, lastDay] = periodDays(period, wallOf(now, zone).date)
  return {
    firstDay,
    lastDay,
    from: wallInstant(firstDay, 0, zone).toISOString(),
    to: wallInstant(addDays(lastDay, 1), 0, zone).toISOString(),
  }
}

/// A club's calendar date read as it is — at UTC midnight — so the
/// browser's own zone never shifts it to the day before.
const calendarDay = (day: string): Date => new Date(`${day}T00:00:00Z`)
const dayMonthYear = new Intl.DateTimeFormat('uk-UA', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })
const dayMonth = new Intl.DateTimeFormat('uk-UA', { day: 'numeric', month: 'long', timeZone: 'UTC' })
const withYear = (day: string): string => dayMonthYear.format(calendarDay(day)).replace(/\s*р\.$/, '')

/// The days a report covers, in words — what «7 днів» actually is today:
/// «28 жовтня 2026», «22–28 жовтня 2026», «29 вересня – 28 жовтня 2026»,
/// «28 грудня 2025 – 3 січня 2026».
export const periodText = (firstDay: string, lastDay: string): string => {
  if (firstDay === lastDay) return withYear(lastDay)
  const sameYear = firstDay.slice(0, 4) === lastDay.slice(0, 4)
  if (sameYear && firstDay.slice(5, 7) === lastDay.slice(5, 7)) return `${Number(firstDay.slice(8, 10))}–${withYear(lastDay)}`
  return `${sameYear ? dayMonth.format(calendarDay(firstDay)) : withYear(firstDay)} – ${withYear(lastDay)}`
}

/// «30 жовтня» — one day without its year, for «на 30 жовтня».
export const dayText = (day: string): string => dayMonth.format(calendarDay(day))

/// Whose calendar a report is cut by: the chosen club's, else the first club
/// the caller sees, else Kyiv's — the same order as «Послуги»
/// (services-shell.tsx). `branchId` null — no club chosen.
export const reportZoneOf = (clubs: readonly Pick<Branch, 'id' | 'timezone'>[], branchId: string | null): string => {
  const chosen = branchId === null ? undefined : clubs.find((club) => club.id === branchId)
  return canonicalZone(chosen?.timezone ?? clubs[0]?.timezone ?? DEFAULT_ZONE)
}
